﻿<template>
  <items-listing-layout class="mailbox-home">
    <template #header-left>
      <!-- HEADER TITLE -->
      <listing-header
        :is-loading="isLoadingInitialData"
        :is-custom-filter-submitting="isCustomFilterSubmitting"
        :title="name"
        :entity-id="id"
        :entity-type="EEntities.MAILBOXES"
        :default-filters="defaultFilterNavItems"
        :custom-filters="customFilterNavItems"
        :current-filter="currentFilterNavItem"
        :total-item-count="totalItemCount"
        :active-assignee-counts="activeAssigneeCounts"
        @save-new-filter="(name) => createCustomFilter(name)"
        @update-custom-filter-name="(name) => updateCustomFilter(name)"
        @delete-custom-filter="(id) => deleteCustomFilter(id)"
        @change-filter="(id) => updateItemTraitSetFromFilterID(id)"
        @select-assignee="(id) => selectAssigneeFilter(id)"
      />
    </template>
    <template #header-right>
      <!-- HEADER OPTIONS -->
      <mailbox-home-header-options
        v-if="$screen.isTabletOrDesktop($mq)"
        :is-loading="isLoadingInitialData"
        :members="members"
        :account-id="accountID"
        :mailbox-id="id"
        :mailbox-name="name"
        :is-direct-connection="isDirectConnection"
        :total-item-count="totalItemCount"
        @members-added="getMembers"
      />
    </template>
    <template #main-header>
      <!-- MAIN HEADER -->
      <item-filters-panel
        v-model:search-term="itemTraitSet.searchTerm"
        v-model:selected-assignee-ids="itemTraitSet.assigneeIDs"
        v-model:assigned-not-to-you="itemTraitSet.assignedNotToYou"
        v-model:selected-job-appointee-groupings-string="itemTraitSet.jobAppointeeGroupingsString"
        v-model:selected-customer-ids="itemTraitSet.customerIDs"
        v-model:selected-status-ids="itemTraitSet.statusIDs"
        v-model:status-type="itemTraitSet.statusType"
        v-model:selected-priority-ids="itemTraitSet.priorityIDs"
        v-model:selected-due-type="itemTraitSet.dueType"
        v-model:selected-including-tag-ids="itemTraitSet.includingTagIDs"
        v-model:selected-excluding-tag-ids="itemTraitSet.notIncludingTagIDs"
        v-model:created-within-days="itemTraitSet.createdWithinDays"
        v-model:last-updated-within-days="itemTraitSet.lastUpdatedWithinDays"
        v-model:only-watching="itemTraitSet.onlyWatching"
        v-model:is-spam="itemTraitSet.isSpam"
        v-model:start-due-date="itemTraitSet.startDueDate"
        v-model:end-due-date="itemTraitSet.endDueDate"
        v-model:selected-sort="itemTraitSet.sort"
        v-model:custom-field-filters-string="itemTraitSet.customFieldFiltersString"
        :single-mailbox-id="id"
        :is-disabled="hasSelectedRows"
        :is-loading="isLoadingInitialData"
        :is-default-item-trait-set="isDefaultItemTraitSet"
        :assignee-options="members"
        :job-options="jobs"
        :priority-options="priorities"
        :active-status-options="activeStatuses"
        :inactive-status-options="inactiveStatuses"       
        :tag-options="tags"
        :due-date-options="dueDateOptions"
        :custom-field-options="customFieldsForFiltering"
        @reset-filters="resetItemTraitSet"
        @switch-to-kanban="switchToKanban"
        @settings="handleSettings"
      />
    </template>
    <template #main-content>
      <!-- ITEMS -->
      <home-table
        ref="homeTable"
        :type="EEntities.MAILBOXES"
        :table-data="results"
        :custom-fields="customFields"
        :is-loading="isLoadingResults"
        :class="{'mailbox-home__table-no-page-buffer': !hasPages}"
        class="mailbox-home__table"
        from-entity-home
        @select-rows="updateSelectedRows"
      />
      <!-- PAGINATION -->
      <el-pagination
        v-if="hasPages"
        :page-size="itemsPerPage"
        :pager-count="5"
        :current-page="currentPage"
        :total="totalItemCount"
        background
        layout="prev, pager, next"
        class="mailbox-home__pagination"
        @current-change="(page) => changePage(page)"
      />
    </template>
    <template #modals>
      <!-- BULK EDIT (DESKTOP) -->      
      <bulk-edit-panel
        v-if="hasSelectedRows" 
        :selected-ids="selectedItemIDs"
        :selected-entity-ids="[id]"
        :entity-type="EEntities.MAILBOXES"
        :is-spam-filter-selected="itemTraitSet.isSpam"
        @edit-canceled="($refs.homeTable as { clearBulkEdit(): void }).clearBulkEdit()"
        @edit-completed="reloadData()"
        @move-items-clicked="showMoveItemsModal=true"
        @add-comment-clicked="showAddCommentModal=true"
        @merge-convos-clicked="showMergeConvosModal=true"
      />
      <!-- MOVE MAILBOX -->
      <bulk-move-items-modal
        v-if="showMoveItemsModal"
        :entity-id="id"
        :selected-item-ids="selectedItemIDs"
        is-convo-related
        @edit-canceled="showMoveItemsModal=false"
        @edit-completed="reloadData();showMoveItemsModal=false;"
      />
      <!-- MERGE CONVOS -->
      <bulk-merge-conversations-modal
        v-if="showMergeConvosModal"
        :mailbox-id="id"
        :selected-convo-ids="selectedItemIDs"
        @edit-canceled="showMergeConvosModal=false"
        @edit-completed="reloadData();showMergeConvosModal=false;"
      />
      <!-- BULK ADD COMMENT -->
      <bulk-add-comment-modal
        v-if="showAddCommentModal"
        :selected-item-ids="selectedItemIDs"
        is-convo-related
        @edit-canceled="showAddCommentModal=false"
        @edit-completed="reloadData();showAddCommentModal=false;"
      />
      <!-- MOBILE NEW BUTTON -->
      <div v-if="$screen.isMobile($mq) && hasResults" @click="sendToNewConversation">
        <dd-icon-button
          :class="{'go-native-ios' : $screen.isGoNativeiOSMobileApp($mq)}"
          :color="ColorAssistant.MailboxColor"
          icon="plus"
          class="mailbox-home__create-button"
          type="circle"
          title="Add conversation" 
        />
      </div>
      <!-- MAILBOX INFO -->
      <template v-if="$screen.isDesktop($mq)">
        <entity-info-flyout
          v-if="!isLoadingInitialData"
          v-model:show="showEntityInfoFlyout"
          :entity-id="id"
          :entity-type="EEntities.MAILBOXES"
          :members="members"
          :title="name"
          :workflow="workflow"
        />
      </template>
    </template>
  </items-listing-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import ItemsListingLayout from '@/layouts/items-listing-layout.vue'
import dayjs from '@/helpers/dayjs'

/* Mixins */
import EntityLandingMixin from '@/mixins/entity-landing.mixin'

/* Components */
import MailboxHomeHeaderOptions from './components/mailbox-home-header-options.vue'

/* Helpers */
import LocalStore from '@/helpers/localstore'
import RouteAssistant from '@/helpers/route-assistant'
import BreadcrumbAssistant from '@/helpers/breadcrumb-assistant'
import TextAssistant from '@/helpers/text-assistant'
import ItemFilterAssistant from '@/helpers/item-filter-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { EEntities, SortOptionValues } from '@/types'
import {
  IBreadcrumbItemMenuItem,
  IHomeTableItem,
} from '@/types/data-objects/items'
import { IItemTraitSet } from '@/types/data-objects/listing'
import {
  IConvosOutputSearchConvo,
  ICustomField,
  IMailboxesOutputFilterOptionsForListing,
} from '@/api/types/response.types'
import {
  IMailboxesInputManageCustomFilter,
  IMailboxesInputUpdateCustomFilter,
} from '@/api/types/request.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'
import ConversationsAPI from '@/api/modules/convos.api'
import CustomFiltersAPI from '@/api/modules/custom-filters.api'

export default defineComponent({
  name: 'MailboxHome',

  components: {
    ItemsListingLayout,
    MailboxHomeHeaderOptions,
  },

  extends: EntityLandingMixin,

  data() {
    return {
      ColorAssistant,
      // For entity-landing mixin
      isMailbox: true,
      showMergeConvosModal: false,
      isDirectConnection: false,
      customFields: [] as ICustomField[],
    }
  },

  beforeUnmount() {
    this.endPolling()
  },

  async created() {
    LocalStore.clearMailboxDefaultedToKanban(this.id)

    this.isLoadingInitialData = true
    await Promise.all([
      this.fetchDetails(),
      this.fetchFilterOptionsForListing(),
    ])
    this.isLoadingInitialData = false
    LocalStore.lastItemListRouteVisited = RouteAssistant.getMailboxRoute(
      this.accountID,
      this.id,
      this.name,
    )
    this.startPolling()
    this.postCreated()
    this.fetchDefaultFilterOptionCounts()
    await this.showInfoOnEntityLandingIfNecessary()
  },

  methods: {
    switchToKanban() {
      RouteAssistant.goToMailboxKanban(
        this.$router,
        this.$route.params.accountId,
        this.id,
      )
    },
    handleSettings() {
      RouteAssistant.goToMailboxSettings(
        this.$router,
        this.$route.params.accountId,
        this.id,
      )
    },
    startPolling() {
      // Ensure we're clearing old polling, if necessary
      this.endPolling()
      // A load that finishes after the user left would otherwise start a poller nothing clears
      if (this.isUnmounted) {
        return
      }
      this.poller = setInterval(async () => {
        try {
          await this.autoRefreshDataIfStale()
        } catch {
          this.endPolling()
        }
      }, this.pollIntervalInMS)
    },
    endPolling() {
      clearInterval(this.poller)
    },
    async reloadData() {
      await Promise.all([
        this.fetchFilterOptionsForListing(),
        this.fetchResults(false),
      ])
      this.fetchDefaultFilterOptionCounts()
    },
    async autoRefreshDataIfStale() {
      const lastUpdatedEpochTime = await MailboxesAPI.get_LastUpdatedEpochTime(
        this.id,
      )
      if (
        lastUpdatedEpochTime.epochTime &&
        this.lastAccessEpochTime < lastUpdatedEpochTime.epochTime
      ) {
        await Promise.all([
          this.fetchDetails(),
          this.fetchFilterOptionsForListing(),
          this.fetchResults(false),
        ])
        this.fetchDefaultFilterOptionCounts()
        this.lastAccessEpochTime = lastUpdatedEpochTime.epochTime
      }
    },
    async fetchDetails() {
      try {
        const details = await MailboxesAPI.get_Detail(this.id)
        this.name = details.name
        this.members = details.listPeople
        this.workflow = details.workflow
        this.isDirectConnection = details.isDirectConnection
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async fetchResults(showResultsLoading: boolean = true) {
      this.selectedItemIDs = []
      this.isLoadingResults = showResultsLoading
      const params = {
        page: this.currentPage,
        priority_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.priorityIDs,
        ),
        status_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.statusIDs,
        ),
        status_type: this.itemTraitSet.statusType,
        assignee_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.assigneeIDs,
        ),
        assigned_not_to_you: this.itemTraitSet.assignedNotToYou,
        job_appointee_groupings_string: this.itemTraitSet
          .jobAppointeeGroupingsString,
        including_tag_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.includingTagIDs,
        ),
        not_including_tag_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.notIncludingTagIDs,
        ),
        start_due_date: this.itemTraitSet.startDueDate,
        end_due_date: this.itemTraitSet.endDueDate,
        due_type: this.itemTraitSet.dueType,
        sort: this.itemTraitSet.sort,
        search_term: this.itemTraitSet.searchTerm,
        only_watching: this.itemTraitSet.onlyWatching,
        created_within_days: this.itemTraitSet.createdWithinDays,
        last_updated_within_days: this.itemTraitSet.lastUpdatedWithinDays,
        custom_field_filters: this.itemTraitSet.customFieldFiltersString,
        customer_ids: TextAssistant.convertNumberArrayToStringList(
          this.itemTraitSet.customerIDs,
        ),
        is_spam: this.itemTraitSet.isSpam,
      }

      try {
        // If hiding the load, don't reset total item count so there
        // is no "pulse" on the dashboard filter picker
        if (showResultsLoading) {
          this.totalItemCount = null
        }
        const response = await ConversationsAPI.get_Search(
          params.page,
          this.id.toString(),
          params.customer_ids,
          params.sort,
          params.is_spam,
          params.priority_ids,
          params.status_ids,
          params.status_type,
          params.assignee_ids,
          params.assigned_not_to_you,
          params.job_appointee_groupings_string,
          params.including_tag_ids,
          params.not_including_tag_ids,
          params.due_type,
          params.start_due_date,
          params.end_due_date,
          params.search_term,
          params.only_watching,
          params.created_within_days,
          params.last_updated_within_days,
          params.custom_field_filters,
        )

        // Store access epoch time
        this.lastAccessEpochTime = response.accessEpochTime

        // Pagination
        this.totalItemCount = response.totalConversationCount

        // Custom fields flagged to show on listings
        this.customFields = response.listCustomFields || []

        // Results
        // @ts-ignore
        this.results = response.listConversations.map(item =>
          this.mapResultsToTableFormat(item),
        )

        const breadCrumbItems = [] as IBreadcrumbItemMenuItem[]

        response.listConversations.forEach(t => {
          breadCrumbItems.push({
            itemID: t.id,
            entityID: this.id,
            entityName: this.name,
            entityType: EEntities.MAILBOXES,
            title: t.title,
            refNumber: t.refNumber,
          } as IBreadcrumbItemMenuItem)
        })

        BreadcrumbAssistant.cacheBreadcrumbItemMenuItems(
          breadCrumbItems,
        )
      } catch (error) {
        LocalStore.clearHomeSavedQuery(this.id)
        this.$error.emit(error)
      } finally {
        this.isLoadingResults = false
      }
    },

    async fetchFilterOptionsForListing() {
      try {
        const response = await MailboxesAPI.get_FilterOptionsForListing(this.id)
        this.hydrateFilterOptionsForListing(response)
      } catch (error) {
        this.$error.emit(error)
      }
    },

    async fetchDefaultFilterOptionCounts() {
      try {
        // Swallow if timeout (or other error) occurs...
        const counts = await MailboxesAPI.get_DefaultFilterOptionCounts(this.id)
        this.updateDefaultFilterOptionCounts(
          counts.listDefaultFilters,
          counts.listActiveAssigneeCounts,
        )
      } catch (error) {
        // Swallow
      }
    },

    hydrateFilterOptionsForListing(
      data: IMailboxesOutputFilterOptionsForListing,
    ) {
      this.hydrateFilterOptionsForListing_Base(data)
      this.customFilterNavItems = data.listCustomFilters.map(c => ({
        value: ItemFilterAssistant.getCustomFilterIdentifier(c.id),
        label: c.name,
        icon: 'filter',
      }))
      this.customFilterItemTraitSets = data.listCustomFilters.map(c => ({
        id: c.id,
        itemTraitSet: {
          priorityIDs: c.listPriorityIDs ? c.listPriorityIDs : [],
          statusIDs: c.listStatusIDs ? c.listStatusIDs : [],
          statusType: c.statusType ? c.statusType : '',
          assigneeIDs: c.listAssigneeIDs ? c.listAssigneeIDs : [],
          assignedNotToYou: c.assignedNotToYou,
          jobAppointeeGroupingsString: ItemFilterAssistant.convertJobAppointeeGroupingsToStringRepresentation(
            c.listJobAppointeeGroupings,
          ),
          dueType: c.dueType ? c.dueType : null,
          includingTagIDs: c.listIncludingTagIDs ? c.listIncludingTagIDs : [],
          notIncludingTagIDs: c.listNotIncludingTagIDs
            ? c.listNotIncludingTagIDs
            : [],
          startDueDate: c.startDueDate
            ? dayjs(c.startDueDate).format('YYYY-MM-DD')
            : null,
          endDueDate: c.endDueDate
            ? dayjs(c.endDueDate).format('YYYY-MM-DD')
            : null,
          searchTerm: c.searchTerm ? c.searchTerm : '',
          onlyWatching: c.onlyWatching,
          createdWithinDays: c.createdWithinDays,
          lastUpdatedWithinDays: c.lastUpdatedWithinDays,
          creatorIDs: [], // Never used, default it so the comparison is equal in isItemTraitSetEquivalent()
          customerIDs: c.listCustomerIDs ? c.listCustomerIDs : [],
          isSpam: c.isSpam,
          sort: SortOptionValues.UPDATED_RECENT,
          projectIDs: [], // Unused for mailbox/project home, default it so the comparison is equal in isItemTraitSetEquivalent()
          mailboxIDs: [], // Unused for mailbox/project home, default it so the comparison is equal in isItemTraitSetEquivalent()
          customFieldFiltersString: c.customFieldFiltersString
            ? c.customFieldFiltersString
            : '',
        } as IItemTraitSet,
      }))
    },

    async getMembers() {
      try {
        this.members = await MailboxesAPI.get_Members(this.id)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    async createCustomFilter(name: string) {
      try {
        this.isCustomFilterSubmitting = true
        const formData = {
          name,
          listCustomerIDs: this.itemTraitSet.customerIDs,
          listAssigneeIDs: this.itemTraitSet.assigneeIDs,
          listJobAppointeeGroupings: ItemFilterAssistant.convertStringToJobAppointeeGroupingsRepresentation(
            this.itemTraitSet.jobAppointeeGroupingsString,
          ),
          listIncludingTagIDs: this.itemTraitSet.includingTagIDs,
          listNotIncludingTagIDs: this.itemTraitSet.notIncludingTagIDs,
          listStatusIDs: this.itemTraitSet.statusIDs,
          listPriorityIDs: this.itemTraitSet.priorityIDs,
          createdWithinDays: this.itemTraitSet.createdWithinDays,
          lastUpdatedWithinDays: this.itemTraitSet.lastUpdatedWithinDays,
          assignedNotToYou: this.itemTraitSet.assignedNotToYou,
          statusType: this.itemTraitSet.statusType,
          dueType: this.itemTraitSet.dueType,
          startDueDate: this.itemTraitSet.startDueDate,
          endDueDate: this.itemTraitSet.endDueDate,
          searchTerm: this.itemTraitSet.searchTerm,
          isSpam: this.itemTraitSet.isSpam,
          onlyWatching: this.itemTraitSet.onlyWatching,
          customFieldFiltersString: this.itemTraitSet.customFieldFiltersString,
        } as IMailboxesInputManageCustomFilter
        const response = await MailboxesAPI.post_CreateCustomFilter(
          this.id,
          formData,
        )
        await this.fetchFilterOptionsForListing()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isCustomFilterSubmitting = false
      }
    },

    async updateCustomFilter(name: string) {
      try {
        this.isCustomFilterSubmitting = true
        const formData = {
          name,
          listCustomerIDs: this.itemTraitSet.customerIDs,
          listAssigneeIDs: this.itemTraitSet.assigneeIDs,
          listJobAppointeeGroupings: ItemFilterAssistant.convertStringToJobAppointeeGroupingsRepresentation(
            this.itemTraitSet.jobAppointeeGroupingsString,
          ),
          listIncludingTagIDs: this.itemTraitSet.includingTagIDs,
          listNotIncludingTagIDs: this.itemTraitSet.notIncludingTagIDs,
          listStatusIDs: this.itemTraitSet.statusIDs,
          listPriorityIDs: this.itemTraitSet.priorityIDs,
          createdWithinDays: this.itemTraitSet.createdWithinDays,
          lastUpdatedWithinDays: this.itemTraitSet.lastUpdatedWithinDays,
          assignedNotToYou: this.itemTraitSet.assignedNotToYou,
          statusType: this.itemTraitSet.statusType,
          dueType: this.itemTraitSet.dueType,
          startDueDate: this.itemTraitSet.startDueDate,
          endDueDate: this.itemTraitSet.endDueDate,
          searchTerm: this.itemTraitSet.searchTerm,
          isSpam: this.itemTraitSet.isSpam,
          onlyWatching: this.itemTraitSet.onlyWatching,
          customFieldFiltersString: this.itemTraitSet.customFieldFiltersString,
        } as IMailboxesInputUpdateCustomFilter
        const response = await MailboxesAPI.put_UpdateCustomFilter(
          this.id,
          ItemFilterAssistant.getIDOfFilterIdentifier(
            this.currentFilterNavItem.value,
          ),
          formData,
        )
        await this.fetchFilterOptionsForListing()
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isCustomFilterSubmitting = false
      }
    },

    async deleteCustomFilter(identifier: string) {
      try {
        const response = await CustomFiltersAPI.delete_Delete(
          ItemFilterAssistant.getIDOfFilterIdentifier(identifier),
        )

        this.fetchFilterOptionsForListing()

        if (this.currentFilterNavItem.value !== identifier) {
          this.$messaging.showSuccess(response.message)
        }
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    sendToNewConversation() {
      RouteAssistant.goToCreateConversation(
        this.$router,
        this.accountID,
        this.id,
        this.name,
      )
    },

    mapResultsToTableFormat(item: IConvosOutputSearchConvo): IHomeTableItem {
      return {
        ...item,
        parent: item.mailbox,
        entityType: EEntities.MAILBOXES,
      }
    },
  },
})
</script>

<style lang="scss">
.mailbox-home {
  &__create-button {
    position: fixed;
    right: 15px;
    bottom: 20px;

    @include media-breakpoint-down(sm) {
      bottom: 70px;
      &.go-native-ios {
        bottom: 100px;
      }
    }
  }

  &__table-no-page-buffer {
    /* Accounts for mobile footer menu that happens at small */
    @include media-breakpoint-down(sm) {
      margin-bottom: 90px;
    }
  }

  &__pagination {
    margin-top: 24px;
    text-align: center;

    /* Accounts for mobile footer menu that happens at small */
    @include media-breakpoint-down(sm) {
      margin-bottom: 90px;
    }
  }

  .container.main-container {
    /* For home, "mobile" switch happens at medium */
    @include media-breakpoint-down(md) {
      max-width: 100%;
    }
  }
}
</style>
